import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    signal,
    WritableSignal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { AutoFocus } from 'primeng/autofocus';
import { InputNumber } from 'primeng/inputnumber';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import {
    Step,
    StepList,
    StepPanel,
    StepPanels,
    Stepper,
} from 'primeng/stepper';
import { GameKind, GameTemplate } from '@/lib-backend';
import * as game from '@/lib-backend/methods/game';
import { GameTemplatesService } from '@/app/services/game-templates.service';
import { gameModeLabel, winModeLabel } from '@/app/utils/game';
import { resolveGameContentUrl } from '@/app/utils/game-content-url';
import { environment } from '@/environments/environment';
import { ImagePreviewService } from '@/app/services/image-preview.service';
import { ProductBaseArtPanel } from '../product-base-art-dialog/product-base-art-panel';
import { SharedContentService } from '@/app/services/shared-content.service';
import { NotificationService } from '@/app/services/notification.service';
import { AuthService } from '@/app/services/auth.service';
import {
    AiFinalizeGameArt,
    FinalizedArtApplied,
} from '@/app/components/ai-finalize-game-art';
import { AiPagesGenerate } from '@/app/components/ai-pages-generate';

/** Template-document fields edited on the General tab. */
interface GeneralDraft {
    Title: string;
    Description: string;
    GameKindId: string;
    /** Comma-separated tags; split on save. */
    TagsText: string;
    IsActive: boolean;
    IsFeatured: boolean;
    HasMinEntryFee: boolean;
    MinEntryFee: number | null;
}

/** Template-document default art edited on the Art tab. */
interface ArtDraft {
    IconUrl: string;
    BannerUrl: string;
    /** Textarea content, one URL per line. */
    ImagesText: string;
}

const EMPTY_GENERAL: GeneralDraft = {
    Title: '',
    Description: '',
    GameKindId: '',
    TagsText: '',
    IsActive: true,
    IsFeatured: false,
    HasMinEntryFee: false,
    MinEntryFee: null,
};

const EMPTY_ART: ArtDraft = {
    IconUrl: '',
    BannerUrl: '',
    ImagesText: '',
};

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

@Component({
    selector: 'app-template-edit-dialog',
    standalone: true,
    imports: [
        NgTemplateOutlet,
        FormsModule,
        AutoFocus,
        Button,
        Checkbox,
        Dialog,
        InputText,
        InputNumber,
        Message,
        Select,
        Textarea,
        Tab,
        TabList,
        TabPanel,
        TabPanels,
        Tabs,
        Step,
        StepList,
        StepPanel,
        StepPanels,
        Stepper,
        ProductBaseArtPanel,
        AiFinalizeGameArt,
        AiPagesGenerate,
    ],
    templateUrl: './template-edit-dialog.html',
})
export class TemplateEditDialog implements OnInit {
    @Input({ required: true }) set visible(v: boolean) {
        this.visibleSignal.set(v);
    }

    @Input() set template(t: GameTemplate | null) {
        this.editingTemplate.set(t);
    }

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() saved = new EventEmitter<void>();

    protected readonly templatesService = inject(GameTemplatesService);
    private readonly sharedContent = inject(SharedContentService);
    private readonly notification = inject(NotificationService);
    private readonly auth = inject(AuthService);
    protected readonly preview = inject(ImagePreviewService);

    protected readonly visibleSignal = signal(false);
    protected readonly editingTemplate = signal<GameTemplate | null>(null);
    /** In Add mode, the template once it has been created by the General step. */
    protected readonly createdTemplate = signal<GameTemplate | null>(null);

    protected readonly generalDraft = signal<GeneralDraft>({
        ...EMPTY_GENERAL,
    });
    private readonly initialGeneral = signal<GeneralDraft>({ ...EMPTY_GENERAL });
    protected readonly artDraft = signal<ArtDraft>({ ...EMPTY_ART });
    private readonly initialArt = signal<ArtDraft>({ ...EMPTY_ART });

    /** Edit-mode tab + Add-mode wizard step. */
    protected readonly activeTab = signal<'general' | 'baseart' | 'art'>(
        'general',
    );
    protected readonly step = signal<number>(1);

    protected readonly saving = signal(false);
    protected readonly saveError = signal<string | null>(null);

    protected readonly iconUploading = signal(false);
    protected readonly bannerUploading = signal(false);
    protected readonly imageUploading = signal(false);
    /** AI finalize (default icon + banner) window. */
    protected readonly aiArtVisible = signal(false);
    /** AI pages (default images) window. */
    protected readonly aiPagesVisible = signal(false);

    /** A game template's URL is always just the environment's games-app host. */
    protected readonly gameUrl = environment.gamesAppBaseUrl;

    /** Uploads / AI write into the shared store — global admins only. */
    protected readonly canUpload = computed(() => this.auth.isGlobalAdmin());

    protected readonly isEdit = computed(() => this.editingTemplate() !== null);

    /** The persisted template, if any — editing target, or created mid-wizard. */
    protected readonly activeTemplate = computed(
        () => this.editingTemplate() ?? this.createdTemplate(),
    );

    protected readonly title = computed(() =>
        this.isEdit() ? 'Edit Game Template' : 'Add Game Template',
    );

    /** The Base Art tab/step only loads once a template exists and is shown. */
    protected readonly baseArtActive = computed(() => {
        if (!this.activeTemplate()) return false;
        return this.isEdit() ? this.activeTab() === 'baseart' : this.step() === 2;
    });

    protected readonly gameKindOptions = computed(() =>
        this.templatesService.gameKinds().map((k) => ({
            value: k.GameKindId,
            label: this.formatGameKind(k),
        })),
    );

    /** Game kind is fixed once the template exists (create-time choice). */
    protected readonly gameKindLocked = computed(
        () => this.activeTemplate() !== null,
    );

    protected readonly canSaveGeneral = computed(() => {
        const d = this.generalDraft();
        if (!d.Title.trim() || !d.GameKindId) return false;
        if (d.HasMinEntryFee && (d.MinEntryFee == null || d.MinEntryFee < 0))
            return false;
        return true;
    });

    /** Art is set only via Upload / AI, so the stored URLs are always valid. */
    protected readonly canSaveArt = computed(() => true);

    private readonly generalDirty = computed(
        () =>
            JSON.stringify(this.generalDraft()) !==
            JSON.stringify(this.initialGeneral()),
    );
    private readonly artDirty = computed(
        () =>
            JSON.stringify(this.artDraft()) !==
            JSON.stringify(this.initialArt()),
    );

    /** Edit-mode footer Save gate. */
    protected readonly canSave = computed(
        () =>
            this.canSaveGeneral() &&
            this.canSaveArt() &&
            (this.generalDirty() || this.artDirty()),
    );

    protected readonly resolveGameContentUrl = resolveGameContentUrl;

    constructor() {
        effect(() => {
            if (this.visibleSignal()) {
                const t = this.editingTemplate();
                const g: GeneralDraft = t
                    ? {
                          Title: t.Title,
                          Description: t.Description,
                          GameKindId: t.GameKindId,
                          TagsText: t.Tags.join(', '),
                          IsActive: t.IsActive,
                          IsFeatured: t.IsFeatured,
                          HasMinEntryFee: t.MinEntryFee != null,
                          MinEntryFee: t.MinEntryFee,
                      }
                    : { ...EMPTY_GENERAL };
                const a: ArtDraft = t
                    ? {
                          IconUrl: t.IconUrl,
                          BannerUrl: t.BannerUrl,
                          ImagesText: t.Images.join('\n'),
                      }
                    : { ...EMPTY_ART };
                this.generalDraft.set({ ...g });
                this.initialGeneral.set({ ...g });
                this.artDraft.set({ ...a });
                this.initialArt.set({ ...a });
                this.createdTemplate.set(null);
                this.activeTab.set('general');
                this.step.set(1);
                this.saveError.set(null);
            }
        });
    }

    ngOnInit(): void {
        this.templatesService.ensureGameKindsLoaded();
    }

    protected setGeneral<K extends keyof GeneralDraft>(
        key: K,
        value: GeneralDraft[K],
    ): void {
        this.generalDraft.update((d) => ({ ...d, [key]: value }));
    }

    protected setArt<K extends keyof ArtDraft>(
        key: K,
        value: ArtDraft[K],
    ): void {
        this.artDraft.update((d) => ({ ...d, [key]: value }));
    }

    protected imagesArray(): string[] {
        return this.artDraft()
            .ImagesText.split('\n')
            .map((s) => s.trim())
            .filter(Boolean);
    }

    protected onImagesChange(images: string[]): void {
        this.setArt('ImagesText', images.join('\n'));
    }

    protected removeImage(url: string): void {
        this.onImagesChange(this.imagesArray().filter((u) => u !== url));
    }

    // ── Tab 3: uploads + AI default art ──────────────────────────────────────

    protected async uploadIcon(target: EventTarget | null): Promise<void> {
        const url = await this.uploadArt('icon', this.iconUploading, target);
        if (url) this.setArt('IconUrl', url);
    }

    protected async uploadBanner(target: EventTarget | null): Promise<void> {
        const url = await this.uploadArt('banner', this.bannerUploading, target);
        if (url) this.setArt('BannerUrl', url);
    }

    protected async uploadImage(target: EventTarget | null): Promise<void> {
        const slug = `image-${crypto.randomUUID()}`;
        const url = await this.uploadArt(slug, this.imageUploading, target);
        if (url) {
            this.onImagesChange([...this.imagesArray(), url]);
        }
    }

    /** Upload one file to `products/{gameTemplateId}/{slug}`; returns its URL. */
    private async uploadArt(
        slug: string,
        flag: WritableSignal<boolean>,
        target: EventTarget | null,
    ): Promise<string | null> {
        const input = target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        const template = this.activeTemplate();
        if (!file || !template) return null;
        if (file.size > MAX_UPLOAD_BYTES) {
            const limitMb = (MAX_UPLOAD_BYTES / (1024 * 1024)).toFixed(1);
            this.notification.show({
                severity: 'warn',
                summary: 'File too large',
                detail: `"${file.name}" exceeds the ${limitMb} MB limit.`,
            });
            return null;
        }
        flag.set(true);
        try {
            return await this.sharedContent.uploadImageFile(
                `products/${template.Id}/${slug}`,
                file,
            );
        } finally {
            flag.set(false);
        }
    }

    protected openAiArt(): void {
        if (!this.canUpload() || !this.activeTemplate()) return;
        this.aiArtVisible.set(true);
    }

    protected onAiArtApplied(applied: FinalizedArtApplied): void {
        this.setArt('IconUrl', applied.iconUrl);
        this.setArt('BannerUrl', applied.bannerUrl);
    }

    protected openAiPages(): void {
        if (!this.canUpload() || !this.activeTemplate()) return;
        this.aiPagesVisible.set(true);
    }

    protected onAiPagesApplied(urls: string[]): void {
        this.setArt('ImagesText', urls.join('\n'));
    }

    protected onVisibleChange(value: boolean): void {
        this.visibleSignal.set(value);
        this.visibleChange.emit(value);
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    // ── Add wizard ───────────────────────────────────────────────────────────

    /** General step: create the template (once) then advance to Art. */
    protected async createAndContinue(): Promise<void> {
        if (this.activeTemplate()) {
            this.step.set(2);
            return;
        }
        if (!this.canSaveGeneral() || this.saving()) return;
        this.saving.set(true);
        this.saveError.set(null);
        try {
            const result = await this.templatesService.createTemplate(
                this.buildCreate(),
            );
            if (result.kind === 'ok') {
                this.createdTemplate.set(result.template.Template);
                // The created entity is now the baseline for the dirty check.
                this.initialGeneral.set({ ...this.generalDraft() });
                this.saved.emit();
                this.step.set(2);
            } else {
                this.saveError.set(this.formatError(result.reason));
            }
        } finally {
            this.saving.set(false);
        }
    }

    /** Art step: persist the default art onto the created template and finish. */
    protected async finishAdd(): Promise<void> {
        const t = this.activeTemplate();
        if (!t || this.saving() || !this.canSaveArt()) return;
        this.saving.set(true);
        this.saveError.set(null);
        try {
            const result = await this.templatesService.updateTemplate(
                this.buildUpdate(t),
            );
            if (result.kind === 'ok') {
                this.saved.emit();
                this.close();
            } else {
                this.saveError.set(this.formatError(result.reason));
            }
        } finally {
            this.saving.set(false);
        }
    }

    // ── Edit save ────────────────────────────────────────────────────────────

    protected async save(): Promise<void> {
        const t = this.editingTemplate();
        if (!t || !this.canSave() || this.saving()) return;
        this.saving.set(true);
        this.saveError.set(null);
        try {
            const result = await this.templatesService.updateTemplate(
                this.buildUpdate(t),
            );
            if (result.kind === 'ok') {
                this.saved.emit();
                this.close();
            } else {
                this.saveError.set(this.formatError(result.reason));
            }
        } finally {
            this.saving.set(false);
        }
    }

    private buildCreate(): game.CreateGameTemplateRequest {
        const g = this.generalDraft();
        const a = this.artDraft();
        return {
            Title: g.Title,
            Description: g.Description,
            GameUrl: this.gameUrl,
            GameKind: g.GameKindId,
            BannerUrl: a.BannerUrl,
            IconUrl: a.IconUrl,
            Images: this.splitImages(a.ImagesText),
            Tags: this.splitTags(g.TagsText),
            IsActive: g.IsActive,
            IsFeatured: g.IsFeatured,
            MinEntryFee: g.HasMinEntryFee ? g.MinEntryFee : null,
        };
    }

    private buildUpdate(t: GameTemplate): game.UpdateGameTemplateRequest {
        const g = this.generalDraft();
        const a = this.artDraft();
        return {
            TemplateId: t.Id,
            Title: g.Title,
            Description: g.Description,
            GameUrl: this.gameUrl,
            GameKind: g.GameKindId,
            BannerUrl: a.BannerUrl,
            IconUrl: a.IconUrl,
            Images: this.splitImages(a.ImagesText),
            Tags: this.splitTags(g.TagsText),
            IsActive: g.IsActive,
            IsFeatured: g.IsFeatured,
            MinEntryFee: g.HasMinEntryFee ? g.MinEntryFee : null,
            ClearMinEntryFee: !g.HasMinEntryFee,
        };
    }

    private splitImages(text: string): string[] {
        return text
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean);
    }

    private splitTags(text: string): string[] {
        return text
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
    }

    private formatGameKind(k: GameKind): string {
        const mode = gameModeLabel(k.GameMode);
        const win = winModeLabel(k.WinMode);
        return `${k.Name} — ${mode} · ${win}`;
    }

    private formatError(reason: string): string {
        switch (reason) {
            case 'GameKindNotFound':
                return 'Selected game kind is not registered on the game server.';
            case 'NotFound':
                return 'Template not found.';
            default:
                return 'Save failed. Please try again.';
        }
    }
}
